type AccessTagFormItem = {
    id?: number;
    item?: string;
    requires_input?: boolean | number | string;
    value?: string | number | null;
};

type AccessTagsFormValues = {
    move?: {
        pickup_access?: AccessTagFormItem[];
        delivery_access?: AccessTagFormItem[];
    };
};

function requiresInput(item: AccessTagFormItem): boolean {
    return item.requires_input === true || item.requires_input === 1 || item.requires_input === '1';
}

/**
 * Mirrors PHP `is_numeric` for the values Book sends as strings/numbers.
 */
function isNumericValue(value: string | number): boolean {
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }

    const trimmed = value.trim();
    if (trimmed === '') {
        return false;
    }

    return /^-?\d+(\.\d+)?$/.test(trimmed);
}

/**
 * Mirrors PHP `filter_var($value, FILTER_VALIDATE_INT)`.
 */
function isWholeNumber(value: string | number): boolean {
    if (typeof value === 'number') {
        return Number.isInteger(value);
    }

    const trimmed = value.trim();
    return /^-?\d+$/.test(trimmed);
}

/**
 * Client-side access-tag value rules aligned with `AccessCanBeNullRule`.
 * Returns a vee-validate error map keyed by field path.
 */
export function validateAccessTagValues(
    items: AccessTagFormItem[] | undefined,
    fieldPath: 'pickup_access' | 'delivery_access',
): Record<string, string> {
    const errors: Record<string, string> = {};

    (items ?? []).forEach((item, index) => {
        if (!requiresInput(item)) {
            return;
        }

        const key = `move.${fieldPath}.${index}.value`;
        const value = item.value;

        // Match PHP `!$value` — empty / null / 0 fail the required check.
        if (value === null || value === undefined || value === '' || value === 0 || value === '0') {
            errors[key] = 'This field is required.';
            return;
        }

        if (!isNumericValue(value)) {
            errors[key] = 'Value must be a number.';
            return;
        }

        if (item.item === 'Stairs' && !isWholeNumber(value)) {
            errors[key] = 'Value must be a whole number.';
        }
    });

    return errors;
}

/**
 * Vee-validate TypedSchema wrapping {@link validateAccessTagValues}.
 * A plain `(values) => errors` function is NOT accepted by vee-validate —
 * it falls through to validateObjectSchema and never runs custom logic.
 */
export function createAccessTagsValidationSchema(fieldPath: 'pickup_access' | 'delivery_access') {
    return {
        __type: 'VVTypedSchema' as const,
        async parse(values: AccessTagsFormValues) {
            const fieldErrors = validateAccessTagValues(values?.move?.[fieldPath], fieldPath);
            const errors = Object.entries(fieldErrors).map(([path, message]) => ({
                path,
                errors: [message],
            }));

            return {
                value: values,
                errors,
            };
        },
    };
}
